import React from 'react'
import { Checkbox, FormControlLabel } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { MARKETING_EMAIL_CHECKBOX_LABEL } from '../../consent/consentCopy'

export interface MarketingEmailCheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  id?: string
  /**
   * Lets a caller on a surface that doesn't follow the tokens restyle the colours from its own
   * stylesheet; a scoped SCSS rule out-specifies the token `sx` below.
   */
  className?: string
  sx?: SxProps<Theme>
}

const checkboxColorSx = {
  color: 'var(--sevv-text-secondary, #757575)',
  '&.Mui-checked': { color: 'var(--sevv-primary, #f97415)' },
}

/**
 * The marketing email opt-in (wording version `v1`). Controlled: the caller decides the
 * initial value — unticked at sign-up, the recorded choice on the Terms screen.
 *
 * Every place this renders is a light surface while the MUI theme's text colour is built for
 * the dark in-game canvas, so the colours are always stated explicitly, from the Environment
 * tokens (see `className` for surfaces that don't follow them).
 */
const MarketingEmailCheckbox: React.FC<MarketingEmailCheckboxProps> = ({
  checked,
  onChange,
  disabled,
  id = 'marketing-email-opt-in',
  className,
  sx,
}) => (
  <FormControlLabel
    className={className}
    sx={[
      {
        alignItems: 'flex-start',
        mx: 0,
        textAlign: 'left',
        '& .MuiFormControlLabel-label': {
          fontSize: '0.875rem',
          lineHeight: 1.5,
          pt: '9px',
          color: 'var(--sevv-text-primary, #424242)',
        },
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    control={
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        sx={checkboxColorSx}
      />
    }
    label={MARKETING_EMAIL_CHECKBOX_LABEL}
  />
)

export default MarketingEmailCheckbox
